/**
 * Helpers for validating and inferring file extensions on user-supplied files.
 *
 * The upload pipeline rejects files that have no extension, since downstream
 * processing keys off the extension to pick the right codec / processor. When
 * the browser already reports a recognizable MIME type we can rename the file
 * automatically; otherwise the UI prompts the user to supply one.
 */

export const MISSING_EXTENSION_MESSAGE = "Files you upload must have an extension, for example .jpeg";

const VALID_EXTENSION_PATTERN = /^\w{1,10}$/;

const MIME_TO_EXTENSION: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/pjpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/avif": "avif",
  "image/svg+xml": "svg",
  "image/bmp": "bmp",
  "image/tiff": "tiff",
  "audio/mpeg": "mp3",
  "audio/mp3": "mp3",
  "audio/mp4": "m4a",
  "audio/x-m4a": "m4a",
  "audio/m4a": "m4a",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/wave": "wav",
  "audio/ogg": "ogg",
  "audio/webm": "webm",
  "audio/aac": "aac",
  "audio/flac": "flac",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/x-msvideo": "avi",
  "video/webm": "webm",
  "video/x-matroska": "mkv",
  "text/plain": "txt",
  "text/csv": "csv",
  "text/html": "html",
  "application/pdf": "pdf",
  "application/json": "json",
  "application/zip": "zip",
  "application/x-zip-compressed": "zip",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
};

/**
 * Pull the trailing extension off a filename, lower-cased and without the dot.
 *
 * Returns "" when the filename has no extension or when the trailing token
 * doesn't look like a real extension (too long, contains punctuation, etc.).
 * A leading-dot file like ".env" is treated as having no extension.
 */
export const extractFileExtension = (filename: string): string => {
  if (!filename) return "";
  const dot = filename.lastIndexOf(".");
  if (dot < 1 || dot === filename.length - 1) return "";
  const candidate = filename.slice(dot + 1);
  return VALID_EXTENSION_PATTERN.test(candidate) ? candidate.toLowerCase() : "";
};

/**
 * Map a browser-reported MIME type to a canonical extension, or "" if we
 * don't have a mapping for it.
 */
export const inferExtensionFromMimeType = (mimeType?: string): string => {
  if (!mimeType) return "";
  const normalized = mimeType.toLowerCase().split(";")[0]?.trim() ?? "";
  return MIME_TO_EXTENSION[normalized] ?? "";
};

/** Canonical MIME for a filename extension; empty when unknown. */
const EXTENSION_TO_MIME: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  avif: "image/avif",
  svg: "image/svg+xml",
  bmp: "image/bmp",
  tiff: "image/tiff",
  tif: "image/tiff",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  wav: "audio/wav",
  ogg: "audio/ogg",
  webm: "video/webm",
  aac: "audio/aac",
  flac: "audio/flac",
  mp4: "video/mp4",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
  txt: "text/plain",
  csv: "text/csv",
  html: "text/html",
  htm: "text/html",
  pdf: "application/pdf",
  json: "application/json",
  zip: "application/zip",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};

/**
 * Map a filename extension (no dot) to a MIME type, or "" if unknown.
 */
export const inferMimeTypeFromExtension = (extension: string): string => {
  if (!extension) return "";
  return EXTENSION_TO_MIME[extension.toLowerCase()] ?? "";
};

export type UploadMimeParts = { filetype: string; type: string; codec: string };

/**
 * Resolve TUS `filetype` and meta `type`/`codec`: prefer a non-empty browser
 * `File.type`, otherwise infer from the filename extension.
 */
export const resolveUploadMimeParts = (file: File): UploadMimeParts => {
  const normalizedType = file.type?.split(";")[0]?.trim().toLowerCase() ?? "";
  const hasReliableBrowserMime = normalizedType.length > 0 && normalizedType !== "application/octet-stream";
  if (hasReliableBrowserMime) {
    const [t, ...codecParts] = normalizedType.split("/");
    return {
      filetype: normalizedType,
      type: t ?? "",
      codec: codecParts.join("/"),
    };
  }
  const fromExt = inferMimeTypeFromExtension(extractFileExtension(file.name));
  if (fromExt) {
    const [t, ...codecParts] = fromExt.split("/");
    return {
      filetype: fromExt,
      type: t ?? "",
      codec: codecParts.join("/"),
    };
  }
  const fallback = normalizedType || "application/octet-stream";
  const [t, ...codecParts] = fallback.split("/");
  return {
    filetype: fallback,
    type: t ?? "",
    codec: codecParts.join("/"),
  };
};

/**
 * Normalize a user-typed extension. Strips leading dots and whitespace and
 * lower-cases the result; returns "" if the value isn't a plausible extension.
 */
export const normalizeUserSuppliedExtension = (input: string): string => {
  if (!input) return "";
  const trimmed = input.trim().replace(/^\.+/, "").toLowerCase();
  return VALID_EXTENSION_PATTERN.test(trimmed) ? trimmed : "";
};

/**
 * Append `.${extension}` to the file's name, returning a fresh File with the
 * original bytes preserved. Falls back to the original file if File can't be
 * reconstructed (e.g. in environments that don't expose the constructor).
 */
export const renameFileWithExtension = (file: File, extension: string): File => {
  if (!file || !extension) return file;
  const newName = `${file.name}.${extension}`;
  try {
    return new File([file], newName, { type: file.type, lastModified: file.lastModified });
  } catch {
    return file;
  }
};
